import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Checkbox, Modal, Spin, message } from "antd";
import { createPortal } from "react-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { baseURL } from "../../globalUtils/axiosConfig";
import "./DashboardSignoffCell.css";

// The API stores a UTC timestamp; display it in South African local time.
const formatCheckedAt = (value) => {
  if (!value) return "Date and time unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date and time unavailable";

  return `${new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Johannesburg",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date)} (SAST)`;
};

export default function DashboardSignoffCell({
  registerId,
  registerUniqueId,
  checkedAt,
  checkedByName,
  canSignOff,
  onUpdated,
  onConflict,
}) {
  const [savedRecord, setSavedRecord] = useState({ checkedAt, checkedByName });
  const [confirmAction, setConfirmAction] = useState(null); // "sign" | "unsign" | null
  const [saving, setSaving] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(null);

  // Keep each row synchronized with the authoritative Dashboard GET response.
  useEffect(() => {
    setSavedRecord({ checkedAt, checkedByName });
  }, [checkedAt, checkedByName]);

  if (!canSignOff) return null;

  const checked = Boolean(savedRecord.checkedAt);
  const removing = confirmAction === "unsign";
  const registerLabel = registerUniqueId || registerId;

  // The tooltip is attached to a real, enabled span (rather than the native
  // checkbox input). This ensures hover works for both checked and disabled
  // loading states and after a refresh or successful PATCH request.
  // Render the tooltip in document.body so Dashboard table overflow,
  // CSS stacking contexts, and Ant Design tooltip styles cannot hide it.
  const showTooltip = (event) => {
    if (!checked || saving || confirmAction) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 310;
    const left = Math.max(
      12,
      Math.min(bounds.left + bounds.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 12)
    );
    setHoverPosition({
      left,
      top: bounds.top >= 120 ? bounds.top - 8 : bounds.bottom + 8,
      above: bounds.top >= 120,
    });
  };

  const hideTooltip = () => setHoverPosition(null);

  const signoffDetails = (
    <>
      <div style={{ fontWeight: 600, marginBottom: 5, color: "#ed017f" }}>Sign-off details</div>
      <div>
        <strong>Signed off by:</strong> {savedRecord.checkedByName || "Name unavailable"}
      </div>
      <div>
        <strong>Date and time:</strong> {formatCheckedAt(savedRecord.checkedAt)}
      </div>
    </>
  );

  const askForConfirmation = (event) => {
    event?.stopPropagation?.();
    if (saving) return;
    hideTooltip();
    setConfirmAction(checked ? "unsign" : "sign");
  };

  const closeConfirmation = () => {
    if (!saving) setConfirmAction(null);
  };

  const submitConfirmation = async () => {
    if (saving || !confirmAction) return;
    const action = confirmAction;
    setSaving(true);

    try {
      const url = `${baseURL}/stock-management-register/${registerId}/dashboard-signoff`;
      const response = action === "unsign" ? await axios.delete(url) : await axios.patch(url);
      const result = response.data;
      const updated = {
        checkedAt: result.dashboardCheckedAt ?? null,
        checkedByName: result.dashboardCheckedByName ?? null,
      };

      setSavedRecord(updated);
      onUpdated?.(registerId, result);
      setConfirmAction(null);
      message.success(action === "unsign" ? "Dashboard sign-off removed" : "Dashboard entry signed off");
    } catch (error) {
      if (error.response?.status === 409) {
        setConfirmAction(null);
        message.warning("This Dashboard sign-off has changed. Refreshing the entry.");
        onConflict?.();
      } else {
        const details = error.response?.data?.message;
        message.error(
          Array.isArray(details)
            ? details.join(", ")
            : details || "Unable to update Dashboard sign-off"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <span
          className="dashboard-signoff-checkbox-container"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "5px 8px",
            cursor: saving ? "wait" : "pointer",
          }}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <Checkbox
            checked={checked}
            disabled={saving}
            onChange={askForConfirmation}
            aria-label={checked ? "Remove Dashboard sign-off" : "Sign off Dashboard entry"}
          />
        </span>
      {checked && hoverPosition && !confirmAction && createPortal(
        <div
          role="tooltip"
          style={{
            position: "fixed",
            left: hoverPosition.left,
            top: hoverPosition.top,
            transform: hoverPosition.above ? "translateY(-100%)" : "none",
            width: 310,
            maxWidth: "calc(100vw - 24px)",
            zIndex: 20000,
            pointerEvents: "none",
            background: "#ffffff",
            color: "#333333",
            border: "1px solid #ed017f",
            boxShadow: "0 3px 14px rgba(0, 0, 0, 0.16)",
            borderRadius: 3,
            padding: "10px 12px",
            fontFamily: "poppins, Arial",
            fontSize: 12,
            lineHeight: "1.7",
            overflowWrap: "anywhere",
          }}
        >
          {signoffDetails}
        </div>,
        document.body
      )}

      {/* Match the existing Dashboard Export popup: same Ant Design Modal,
          heading, width, centered message and pink add-doc-btn styling. */}
      <Modal
        title={removing ? "REMOVE SIGN-OFF" : "SIGN OFF"}
        open={Boolean(confirmAction)}
        onCancel={closeConfirmation}
        footer={null}
        width={350}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
      >
        <div style={{ padding: "20px 0", textAlign: "center" }}>
          <p
            style={{
              margin: 0,
              fontSize: "16px",
              color: "#333",
              fontFamily: "poppins, Arial",
            }}
          >
            {removing
              ? `Remove sign-off from Dashboard Register ID ${registerLabel}?`
              : `Confirm sign-off for Dashboard Register ID ${registerLabel}?`}
          </p>
        </div>
        {removing && checked && (
          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eeeeee",
              borderRadius: 2,
              margin: "2px 0 8px",
              padding: "12px 14px",
              textAlign: "left",
              color: "#333",
              fontFamily: "poppins, Arial",
              fontSize: 12,
              lineHeight: "1.8",
              overflowWrap: "anywhere",
            }}
          >
            {signoffDetails}
          </div>
        )}
        <div style={{ textAlign: "center", marginTop: "24px" }}>
          {saving ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: "#ed017f" }} spin />} />
          ) : (
            <Button
              type="primary"
              className="add-doc-btn"
              onClick={submitConfirmation}
              style={{
                background: "#ed017f",
                borderColor: "#ed017f",
                borderRadius: "2px",
                color: "#ffffff",
                fontFamily: "poppins, Arial",
                padding: "8px 24px",
                height: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {removing ? "Remove Sign-off" : "Sign Off"}
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
}
